import { TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { docCategory } from 'pages/LoanSubmission/constants'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { getLoanTrade, updateLoanTrade } from 'services'
import { Checkbox, Tooltip } from 'stories/components'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import { defaultInputs, inputGroupBeforeKeys, inputGroups } from './constant'

export default function LoanTrade() {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<Record<string, any>>({})
  const [changed, setChanged] = useState(false)
  const inputs = defaultInputs()

  const loanNumber = setLoanNumber()

  const init = async () => {
    setLoading(true)
    const res = await getLoanTrade()
    setData(res)
    setLoading(false)
  }

  useEffect(() => {
    init()
  }, [])

  const onChange = (key: string, value: any) => {
    const temp = cloneDeep(data)
    temp[key] = value
    setData(temp)
    setChanged(true)
  }

  const onSubmit = async () => {
    await updateLoanTrade(data)
    toast('Successfully Updated!', { type: 'info' })
    setChanged(false)
  }

  const onAddSubRecord = (groupKey: string) => {
    const temp = cloneDeep(data)
    if (!temp[groupKey]) temp[groupKey] = []
    if (!temp[groupKey].length) temp[groupKey].push(...[{}, {}])
    else temp[groupKey].push({})
    setData(temp)
    setChanged(true)
  }

  const onRemoveSubRecord = (groupKey: string, index: number) => {
    const temp = cloneDeep(data)
    if (!temp[groupKey] || temp[groupKey].length < 2) return
    temp[groupKey].splice(index, 1)
    setData(temp)
    setChanged(true)
  }

  const onSubInputChanged = (key: string, value: any, groupKey: string, index: number) => {
    const temp = cloneDeep(data)
    if (!temp[groupKey]) temp[groupKey] = [{}]
    temp[groupKey][index][key] = value
    setData(temp)
    setChanged(true)
  }

  return (
    <div>
      <LayoutLoading show={loading} />
      <h2 className="text-2xl flex items-center gap-1 font-bold mb-2">
        <span>Post Closing / Loan Trade</span>
      </h2>
      <div className="grid md:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-2">
        {Object.keys(inputs).map((key) => {
          const input = inputs[key]
          input.value = data[key]

          const groupKey = inputGroupBeforeKeys[key]

          return (
            <>
              <div className={`col-span-${input.span || 1}`} key={key}>
                <RenderInput input={input} Key={key} onChange={onChange} />
              </div>
              {groupKey && (
                <>
                  {(data[groupKey] || [{}]).map((item: any, index: number) => {
                    return (
                      <div
                        className="col-span-full border rounded-md p-4 grid md:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-2"
                        key={`${groupKey}-${index}`}
                      >
                        {Object.keys(inputGroups[groupKey]).map((subKey) => {
                          const subInput = cloneDeep(inputGroups[groupKey][subKey])
                          subInput.value = item[subKey]
                          return (
                            <div className={`col-span-${subInput.span || 1}`} key={`${groupKey}-${subKey}-${index}`}>
                              <RenderInput
                                input={subInput}
                                Key={`${groupKey}-${subKey}-${index}`}
                                key={`${groupKey}-${subKey}-${index}`}
                                onChange={(key: string, value: any) =>
                                  onSubInputChanged(subKey, value, groupKey, index)
                                }
                              />
                            </div>
                          )
                        })}
                        <div className="col-span-full flex gap-2">
                          {index === (data[groupKey] || [{}]).length - 1 && (
                            <span
                              className="text-shade-blue cursor-pointer hover:underline"
                              onClick={() => onAddSubRecord(groupKey)}
                            >
                              + Add
                            </span>
                          )}
                          <span
                            className="text-red-500 cursor-pointer hover:underline flex gap-2 items-center"
                            onClick={() => onRemoveSubRecord(groupKey, index)}
                          >
                            <TrashIcon className="w-4 h-4" /> Remove
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </>
              )}
            </>
          )
        })}
        <div className="col-span-full">
          <RenderInput
            input={{
              inputType: 'FileTable',
              title: 'Attachment Files',
              required: false,
              filePath: `loan/${loanNumber}/loanTrade`,
              showCategory: true,
              showStatus: true,
              multiple: true,
              categories: docCategory().filter((v: string) => !!v),
              additionalActions: [],
              value: data.documents,
              additionalRowActions: (document: any, index: number, onChange: Function) => {
                return (
                  <Tooltip message="Internal/External">
                    <Checkbox
                      title=""
                      id={`side-document-intext-${document.id}`}
                      checked={document.intext}
                      onChange={(value: any) => onChange('intext', index, value)}
                    />
                  </Tooltip>
                )
              },
            }}
            Key="documents"
            key="documents"
            onChange={onChange}
          />
        </div>
      </div>
      <SaveChanges show={changed} onSave={onSubmit} />
    </div>
  )
}
